// TIENDA DE PERSONALIZABLES (antes "Packs")
//
// Se paga con DINERO REAL fuera de la plataforma, así que aquí no hay carrito ni
// cantidades: se elige UN producto, la hermandad para la que se quiere, se
// redacta cómo se quiere y se manda una SOLICITUD que el equipo revisa. El pago
// y el seguimiento viven en /jartinet/personalizables/pedidos.
//
// Solo puede solicitar quien sea HERMANO MAYOR de al menos una hermandad: el
// personalizable se hace para la hermandad, no para la persona.
//
// ── CRITERIO VISUAL ─────────────────────────────────────────────────────────
// Usa el design system de la web (oscuro + dorado), pero con un planteamiento
// DIRECTO: precio, stock y qué incluye siempre a la vista —nada plegado tras un
// "ver más"—, un solo botón por tarjeta, textos cortos y contraste alto. La
// prioridad es que se entienda y se pueda usar rápido, no el lucimiento.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { PackProduct, PackOrder, PacksShopConfig, JartinetHermandad } from '@/lib/types';
import { getPacksShopConfig, packsShop_createRequest } from '@/lib/actions';
import { RichText } from '@/components/ui/rich-text';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ImagePasteZone, EstadoBadge, formatEur, hexToRgba, stockInfo, toMs,
} from '@/components/tienda/ui';
import {
  Gem, Loader2, Package, CheckCircle2, ShieldAlert, ArrowRight, Search,
  ChevronLeft, Send, Lock, ShoppingBag, Store, Megaphone, Check, Euro,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TERMINOS_POR_DEFECTO = `Al comprar cualquier ítem extra de pago estás aceptando los términos y condiciones de Jartiland.

- **No se admite ningún tipo de devolución.** Todas las compras son finales.
- Tener elementos de pago **no exime al jugador de sanciones administrativas** en caso de incumplir las normativas del servidor.
- Jartiland no se hace responsable de las pérdidas de los elementos extra por pérdidas de cuenta, baneos o modificaciones del servidor.
- Todos los elementos son extras puramente estéticos: no afectan a la jugabilidad ni al desarrollo de la banda o la hermandad.`;

const PASOS_PROCESO = [
  { t: 'Eliges el producto', d: 'Y la hermandad para la que lo quieres.' },
  { t: 'Cuentas cómo lo quieres', d: 'Con detalle y las fotos de referencia que necesites.' },
  { t: 'Lo revisamos', d: 'Si lo aceptamos, te damos los datos para pagar.' },
  { t: 'Lo creamos', d: 'Sigues el estado y hablas con nosotros por el chat.' },
];

export default function PersonalizablesPage() {
  const { user, jartinetData } = useAuth();
  const [config, setConfig] = useState<PacksShopConfig | null>(null);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<string>('todas');
  const [solicitando, setSolicitando] = useState<PackProduct | null>(null);

  useEffect(() => { getPacksShopConfig().then(setConfig); }, []);

  const productsQuery = useMemo(
    () => query(collection(db, 'packs_products'), orderBy('order', 'asc')),
    [],
  );
  const { data: rawProducts, isLoading } = useCollection<PackProduct>(productsQuery);

  const products = useMemo(
    () => (rawProducts || [])
      .map((p) => sanitizeFirestoreData(p) as PackProduct)
      .filter((p) => p.active !== false),
    [rawProducts],
  );

  // Pedidos propios: solo para el acceso a "Mis pedidos". La consulta FILTRA POR
  // uid a la fuerza (las reglas evalúan el list documento a documento).
  const ordersQuery = useMemo(
    () => (user ? query(collection(db, 'packs_orders'), where('uid', '==', user.uid)) : null),
    [user],
  );
  const { data: rawOrders } = useCollection<PackOrder>(ordersQuery);
  const orders = useMemo(
    () => (rawOrders || [])
      .map((o) => sanitizeFirestoreData(o) as PackOrder)
      .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt)),
    [rawOrders],
  );
  const novedades = useMemo(() => orders.reduce((n, o) => n + (Number(o.unreadUser) || 0), 0), [orders]);
  const ultimo = useMemo(
    () => orders.find((o) => !['entregado', 'cancelado', 'rechazado'].includes(o.status)),
    [orders],
  );

  const misHermandades = useMemo<JartinetHermandad[]>(
    () => (jartinetData?.hermandades || []).filter(
      (h: JartinetHermandad) => Array.isArray(h.hermanosMayores) && !!user && h.hermanosMayores.includes(user.uid),
    ),
    [jartinetData, user],
  );
  const puedePedir = misHermandades.length > 0;

  const categorias = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => set.add(p.category?.trim() || 'Personalizables'));
    return Array.from(set);
  }, [products]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return products.filter((p) => {
      const cat = p.category?.trim() || 'Personalizables';
      if (categoria !== 'todas' && cat !== categoria) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
    });
  }, [products, busca, categoria]);

  const cerrada = config?.open === false;
  const activo = puedePedir && !cerrada;

  return (
    <div className="space-y-6 pb-14">
      {/* ── Cabecera ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-primary/15 via-card/45 to-card/20 p-6 md:p-7"
      >
        <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-5">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border border-primary/30 bg-primary/15 text-primary">
            <Gem className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-headline text-3xl font-bold md:text-4xl">Personalizables</h1>
            <p className="mt-1 text-sm text-muted-foreground md:text-base">
              Enseres únicos hechos a medida para tu hermandad. Los encargas aquí y se pagan con dinero real
              por Bizum o transferencia.
            </p>
          </div>

          <Link to="/jartinet/personalizables/pedidos" className="shrink-0">
            <Button variant="secondary" size="lg" className="relative gap-2 rounded-xl">
              <ShoppingBag className="h-4 w-4" /> Mis pedidos
              {novedades > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-amber-500 px-1 text-[10px] font-bold text-primary-foreground">
                  {novedades}
                </span>
              )}
            </Button>
          </Link>
        </div>

        {/* Estado del último pedido en curso: lo primero que quiere ver quien vuelve */}
        {ultimo && (
          <Link
            to="/jartinet/personalizables/pedidos"
            className="relative mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-white/8 bg-black/25 px-4 py-3 transition-colors hover:border-primary/30"
          >
            <span className="text-sm text-muted-foreground">Tu pedido en curso</span>
            <span className="font-mono text-sm font-bold text-primary">{ultimo.code}</span>
            <span className="truncate text-sm">{ultimo.productName}</span>
            <EstadoBadge status={ultimo.status} className="ml-auto" />
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </Link>
        )}
      </motion.div>

      {/* ── Avisos ── */}
      {config?.announcement && (
        <Aviso icon={Megaphone} color="#facc15">
          <RichText>{config.announcement}</RichText>
        </Aviso>
      )}
      {cerrada && (
        <Aviso icon={Store} color="#f87171" titulo="La tienda está cerrada temporalmente">
          No se admiten solicitudes nuevas ahora mismo. Vuelve pronto.
        </Aviso>
      )}
      {!puedePedir && (
        <Aviso icon={Lock} color="#f59e0b" titulo="Solo el hermano mayor puede encargar">
          Cada personalizable se crea para una hermandad concreta, así que la solicitud tiene que enviarla su
          hermano mayor. Puedes ver el catálogo completo.
        </Aviso>
      )}

      {/* ── Cómo funciona ── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {PASOS_PROCESO.map((s, i) => (
          <div key={i} className="flex gap-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/15 font-headline text-sm font-bold text-primary">
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight">{s.t}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Buscador y categorías ── */}
      {products.length > 0 && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar un personalizable…"
              className="h-11 w-full rounded-xl border border-white/8 bg-card/40 pl-10 pr-4 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/40"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['todas', ...categorias].map((c) => (
              <button
                key={c}
                onClick={() => setCategoria(c)}
                className={`rounded-xl border px-3.5 py-2 text-sm font-semibold transition-all ${
                  categoria === c
                    ? 'border-primary/40 bg-primary/15 text-primary'
                    : 'border-white/8 bg-card/40 text-muted-foreground hover:border-white/15 hover:text-foreground'
                }`}
              >
                {c === 'todas' ? 'Todos' : c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Catálogo ── */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : products.length === 0 ? (
        <Vacio titulo="Todavía no hay personalizables" texto="Estamos preparando el catálogo. Vuelve pronto." />
      ) : visibles.length === 0 ? (
        <Vacio titulo="Sin resultados" texto="Ningún producto coincide con la búsqueda." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visibles.map((p) => (
            <ProductoCard key={p.id} product={p} onPedir={() => setSolicitando(p)} puedePedir={activo} />
          ))}
        </div>
      )}

      {/* ── Términos ── */}
      <section className="rounded-2xl border border-destructive/25 bg-destructive/[0.06] p-5 md:p-6">
        <h2 className="flex items-center gap-2.5 font-headline text-lg font-bold text-destructive">
          <ShieldAlert className="h-5 w-5" /> Términos y condiciones
        </h2>
        <div className="mt-3 text-sm leading-relaxed text-muted-foreground">
          <RichText>{config?.terms || TERMINOS_POR_DEFECTO}</RichText>
        </div>
      </section>

      {solicitando && (
        <SolicitudDialog
          product={solicitando}
          hermandades={misHermandades}
          terms={config?.terms || TERMINOS_POR_DEFECTO}
          onClose={() => setSolicitando(null)}
        />
      )}
    </div>
  );
}

function Aviso({ icon: Icon, color, titulo, children }: {
  icon: React.ElementType; color: string; titulo?: string; children: React.ReactNode;
}) {
  return (
    <div
      className="flex items-start gap-3 rounded-2xl border p-4"
      style={{ borderColor: hexToRgba(color, 0.3), background: hexToRgba(color, 0.09) }}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} />
      <div className="min-w-0 text-sm leading-relaxed text-muted-foreground">
        {titulo && <p className="font-bold" style={{ color }}>{titulo}</p>}
        {children}
      </div>
    </div>
  );
}

function Vacio({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-center">
      <Package className="mb-3 h-10 w-10 text-muted-foreground/30" />
      <p className="font-bold">{titulo}</p>
      <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
    </div>
  );
}

// ─── Tarjeta de producto ────────────────────────────────────────────────────
// Todo a la vista: foto, precio, stock, descripción y qué incluye. Nada plegado.

function ProductoCard({ product, onPedir, puedePedir }: {
  product: PackProduct; onPedir: () => void; puedePedir: boolean;
}) {
  const stock = stockInfo(product.stock);
  const precio = product.priceEur > 0 ? formatEur(product.priceEur) : (product.price || 'A consultar');

  return (
    <motion.article
      whileHover={{ y: -3 }}
      transition={{ type: 'spring', stiffness: 320, damping: 24 }}
      className="flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-card/40"
    >
      <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-black/25">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center bg-gradient-to-br from-primary/10 to-transparent">
            <Gem className="h-10 w-10 text-primary/30" />
          </div>
        )}

        {product.featured && (
          <span className="absolute left-3 top-3 rounded-lg bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground">
            Destacado
          </span>
        )}

        {stock.agotado && (
          <div className="absolute inset-0 grid place-items-center bg-black/70">
            <span className="rounded-lg border border-white/25 px-4 py-1.5 text-sm font-bold uppercase tracking-wider">
              Agotado
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {/* Nombre y precio: lo primero que se lee */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-headline text-lg font-bold leading-tight">{product.name}</h3>
          <span className="shrink-0 whitespace-nowrap font-headline text-xl font-black text-primary">
            {precio}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            className="rounded-lg px-2 py-0.5 text-xs font-bold"
            style={{ color: stock.color, background: hexToRgba(stock.color, 0.14) }}
          >
            {stock.label}
          </span>
          {product.category && (
            <span className="rounded-lg bg-white/6 px-2 py-0.5 text-xs text-muted-foreground">
              {product.category}
            </span>
          )}
        </div>

        {product.description && (
          <div className="text-sm leading-relaxed text-muted-foreground [&_p]:m-0">
            <RichText>{product.description}</RichText>
          </div>
        )}

        {product.includes && product.includes.length > 0 && (
          <ul className="space-y-1.5 rounded-xl border border-white/8 bg-black/20 p-3">
            {product.includes.map((inc, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> {inc}
              </li>
            ))}
          </ul>
        )}

        <div className="flex-1" />

        <Button
          onClick={onPedir}
          disabled={!puedePedir || stock.agotado}
          size="lg"
          className="w-full gap-2 rounded-xl font-bold"
        >
          {stock.agotado
            ? 'Agotado'
            : !puedePedir
              ? <><Lock className="h-4 w-4" /> Solo hermanos mayores</>
              : <>Solicitar <ArrowRight className="h-4 w-4" /></>}
        </Button>
      </div>
    </motion.article>
  );
}

// ─── Diálogo de solicitud (3 pasos) ─────────────────────────────────────────

function SolicitudDialog({ product, hermandades, terms, onClose }: {
  product: PackProduct;
  hermandades: JartinetHermandad[];
  terms: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  // Con una sola hermandad no tiene sentido preguntar: se salta el paso 1.
  const primerPaso = hermandades.length === 1 ? 2 : 1;
  const [paso, setPaso] = useState<1 | 2 | 3>(primerPaso as 1 | 2);
  const [hermandadId, setHermandadId] = useState(hermandades.length === 1 ? hermandades[0].id : '');
  const [mensaje, setMensaje] = useState('');
  const [fotos, setFotos] = useState<string[]>([]);
  const [acepta, setAcepta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState<{ code: string } | null>(null);

  const hermandad = hermandades.find((h) => h.id === hermandadId);
  const precio = product.priceEur > 0 ? formatEur(product.priceEur) : (product.price || 'A consultar');

  const enviar = async () => {
    if (!hermandadId || mensaje.trim().length < 10 || !acepta) return;
    setEnviando(true);
    const res = await packsShop_createRequest({
      productId: product.id,
      hermandadId,
      message: mensaje.trim(),
      images: fotos,
      acceptedTerms: true,
    });
    setEnviando(false);
    if (res?.success) setHecho({ code: res.code });
    else toast({ title: 'No se pudo enviar', description: res?.message, variant: 'destructive' });
  };

  const pasos = [
    ...(hermandades.length === 1 ? [] : [{ n: 1, label: 'Hermandad' }]),
    { n: 2, label: 'Tu encargo' },
    { n: 3, label: 'Confirmar' },
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* Diálogo con formulario [66]: cabecera fija + cuerpo en un div plano con
          min-h-0, nunca ScrollArea, o el botón de enviar se sale de pantalla. */}
      <DialogContent className="flex max-h-[92dvh] max-w-2xl flex-col gap-0 p-0">
        {hecho ? (
          <div className="px-6 py-14 text-center">
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 250, damping: 18 }}
              className="mx-auto grid h-20 w-20 place-items-center rounded-2xl border border-emerald-500/30 bg-emerald-500/15 text-emerald-400"
            >
              <Check className="h-11 w-11" />
            </motion.div>
            <DialogTitle className="mt-5 font-headline text-2xl">Solicitud enviada</DialogTitle>
            <DialogDescription className="mx-auto mt-2 max-w-md text-base">
              Tu solicitud <span className="font-mono font-bold text-primary">{hecho.code}</span> ya está en
              manos del equipo. Te avisaremos por el buzón en cuanto la revisemos.
            </DialogDescription>
            <div className="mt-7 flex flex-wrap justify-center gap-2">
              <Button variant="outline" size="lg" onClick={onClose}>Seguir viendo</Button>
              <Link to="/jartinet/personalizables/pedidos">
                <Button size="lg" className="gap-2">Ver mis pedidos <ArrowRight className="h-4 w-4" /></Button>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-4 px-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/30">
                  {product.imageUrl
                    ? <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                    : <div className="grid h-full w-full place-items-center"><Gem className="h-6 w-6 text-primary/40" /></div>}
                </div>
                <div className="min-w-0 flex-1 pr-6">
                  <DialogTitle className="truncate font-headline text-xl">{product.name}</DialogTitle>
                  <DialogDescription className="flex items-center gap-1.5">
                    Solicitud de compra
                    <span className="inline-flex items-center gap-0.5 font-bold text-primary">
                      <Euro className="h-3.5 w-3.5" />{precio.replace('€', '').trim()}
                    </span>
                  </DialogDescription>
                </div>
              </div>

              {/* Pasos */}
              <div className="flex items-center gap-2">
                {pasos.map((it, i) => (
                  <div key={it.n} className="flex flex-1 items-center gap-2">
                    <span
                      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold transition-colors ${
                        paso >= it.n ? 'bg-primary text-primary-foreground' : 'border border-white/12 text-muted-foreground'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className={`truncate text-xs font-semibold ${paso >= it.n ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                      {it.label}
                    </span>
                    {i < pasos.length - 1 && (
                      <span className={`h-px flex-1 ${paso > it.n ? 'bg-primary/50' : 'bg-white/10'}`} />
                    )}
                  </div>
                ))}
              </div>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-5">
              <AnimatePresence mode="wait">
                {paso === 1 && (
                  <motion.div key="p1" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-3">
                    <p className="text-sm text-muted-foreground">¿Para qué hermandad quieres este personalizable?</p>
                    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                      {hermandades.map((h) => {
                        const sel = h.id === hermandadId;
                        const color = h.colorPrimario || '#facc15';
                        return (
                          <button
                            key={h.id}
                            onClick={() => setHermandadId(h.id)}
                            className="flex items-center gap-3 rounded-2xl border p-3 text-left transition-all"
                            style={{
                              borderColor: sel ? hexToRgba(color, 0.55) : 'rgba(255,255,255,0.08)',
                              background: sel ? hexToRgba(color, 0.12) : 'rgba(255,255,255,0.02)',
                            }}
                          >
                            {h.escudoUrl
                              ? <img src={h.escudoUrl} alt="" className="h-11 w-11 shrink-0 object-contain" />
                              : <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-white/5"><Gem className="h-5 w-5 text-muted-foreground" /></div>}
                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold leading-tight">{h.nombre}</p>
                              <p className="truncate text-xs text-muted-foreground">{h.sede}</p>
                            </div>
                            {sel && <Check className="ml-auto h-5 w-5 shrink-0" style={{ color }} />}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {paso === 2 && (
                  <motion.div key="p2" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-5">
                    <div className="space-y-1.5">
                      <label className="text-sm font-bold">¿Cómo lo quieres?</label>
                      <p className="text-xs text-muted-foreground">
                        Cuanto más detalle nos des, más se parecerá al resultado que tienes en la cabeza:
                        colores, materiales, medidas, en qué te inspiras…
                      </p>
                      <RichTextarea
                        rows={7}
                        value={mensaje}
                        onChange={(e) => setMensaje(e.target.value)}
                        placeholder="Describe con todo detalle cómo quieres el producto…"
                      />
                      <p className="text-right text-[11px] text-muted-foreground/60">
                        {mensaje.trim().length} caracteres {mensaje.trim().length < 10 && '(mínimo 10)'}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold">Fotos de referencia</label>
                      <p className="text-xs text-muted-foreground">
                        Todas las que quieras. Lo más cómodo: copia una imagen y pégala aquí
                        con <kbd className="rounded border border-white/15 bg-white/5 px-1 font-mono text-[10px]">Ctrl</kbd>
                        {' + '}
                        <kbd className="rounded border border-white/15 bg-white/5 px-1 font-mono text-[10px]">V</kbd>.
                      </p>
                      <ImagePasteZone urls={fotos} onChange={setFotos} max={30} />
                    </div>
                  </motion.div>
                )}

                {paso === 3 && (
                  <motion.div key="p3" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-4">
                    <div className="divide-y divide-white/8 rounded-2xl border border-white/8 bg-black/20 px-4">
                      <Resumen k="Producto" v={product.name} />
                      <Resumen k="Hermandad" v={hermandad?.nombre || '—'} />
                      <Resumen k="Precio" v={precio} destacado />
                      <Resumen k="Fotos adjuntas" v={String(fotos.length)} />
                    </div>

                    <div className="rounded-2xl border border-destructive/25 bg-destructive/[0.06] p-4">
                      <p className="flex items-center gap-2 text-sm font-bold text-destructive">
                        <ShieldAlert className="h-4 w-4" /> Términos y condiciones
                      </p>
                      <div className="mt-2 max-h-40 overflow-y-auto pr-1 text-xs leading-relaxed text-muted-foreground">
                        <RichText>{terms}</RichText>
                      </div>
                    </div>

                    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-white/8 bg-card/40 p-4">
                      <Checkbox checked={acepta} onCheckedChange={(v) => setAcepta(v === true)} className="mt-0.5" />
                      <span className="text-sm leading-relaxed">
                        He leído y <strong>acepto los términos y condiciones</strong>. Entiendo que
                        <strong> no se admiten devoluciones</strong>.
                      </span>
                    </label>

                    <p className="rounded-xl bg-white/[0.04] p-3 text-xs leading-relaxed text-muted-foreground">
                      Al enviar la solicitud <strong className="text-foreground">todavía no pagas nada</strong>.
                      Si la aceptamos, te daremos los datos para pagar por Bizum o transferencia
                      desde <em>Mis pedidos</em>.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-2 border-t border-white/8 px-6 py-4">
              {paso > primerPaso ? (
                <Button variant="ghost" className="gap-1.5" onClick={() => setPaso((p) => (p - 1) as 1 | 2 | 3)}>
                  <ChevronLeft className="h-4 w-4" /> Atrás
                </Button>
              ) : (
                <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              )}

              <div className="flex-1" />

              {paso < 3 ? (
                <Button
                  size="lg"
                  className="gap-2"
                  disabled={paso === 1 ? !hermandadId : mensaje.trim().length < 10}
                  onClick={() => setPaso(paso === 1 ? 2 : 3)}
                >
                  Continuar <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button size="lg" className="gap-2 font-bold" disabled={!acepta || enviando} onClick={enviar}>
                  {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Enviar solicitud
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Resumen({ k, v, destacado }: { k: string; v: string; destacado?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <span className="text-sm text-muted-foreground">{k}</span>
      <span className={destacado ? 'font-headline text-lg font-black text-primary' : 'text-sm font-semibold'}>{v}</span>
    </div>
  );
}
